import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton, LinearProgress,
  ToggleButton, ToggleButtonGroup, TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
  Forum as ForumIcon,
  ThumbUpAlt as ThumbUpAltIcon,
  Star as StarIcon,
  Poll as PollIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip as RechartsTooltip,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import PainelNaoConfigurado from '../../components/saude/PainelNaoConfigurado';

// ─── Tipos ───

interface DistItem { nota: number; qtd: number; pct: number; }
interface OpcaoItem { descricao: string; qtd: number; pct: number; }

interface PerguntaNota {
  enunciado: string;
  media: number;
  total: number;
  taxa: number;
  distribuicao: DistItem[];
}

interface PerguntaSelecao {
  enunciado: string;
  total: number;
  opcoes: OpcaoItem[];
}

interface Kpis {
  total_respostas: number;
  pesquisas_ativas: number;
  total_notas: number;
  media_geral: number;
  taxa_satisfacao: number;
}

interface Satisfacao {
  kpis: Kpis;
  notas: PerguntaNota[];
  selecao: PerguntaSelecao[];
  periodo: { inicio: string | null; fim: string | null };
  gerado_em: string;
}

const REFRESH_MS = 60_000;
const COR_PRIMARIA = '#1976d2';
// Verde (satisfeito) -> amarelo -> vermelho (insatisfeito), por nota 0..10.
const corNota = (n: number) => (n >= 7 ? '#43a047' : n >= 5 ? '#ffb300' : '#e53935');
const OPC_COLORS = ['#43a047', '#66bb6a', '#ffb300', '#fb8c00', '#e53935', '#8e24aa', '#00838f', '#546e7a'];

const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const pct = (n?: number | null) => `${(n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const nota = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

type PeriodoOpt = 'tudo' | '30' | '90' | '365' | 'custom';

const isoDaysAgo = (d: number) => new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
const hojeISO = () => new Date().toISOString().slice(0, 10);

// ─── KPI compacto ───

const KpiMini: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color, flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={70} height={30} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Card de pergunta de nota (0-10): media/taxa + histograma ───

const NotaCard: React.FC<{ p: PerguntaNota }> = ({ p }) => {
  const theme = useTheme();
  // Garante todas as notas 0..10 no eixo (0 quando ausente).
  const mapa = new Map(p.distribuicao.map((d) => [d.nota, d]));
  const dados = Array.from({ length: 11 }, (_, n) => ({
    nota: n,
    qtd: mapa.get(n)?.qtd ?? 0,
    pct: mapa.get(n)?.pct ?? 0,
  }));
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{p.enunciado}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: corNota(p.media) }}>{nota(p.media)}<Typography component="span" variant="body2" color="text.secondary">/10</Typography></Typography>
          <Chip size="small" label={`${pct(p.taxa)} satisfeitos (nota ≥ 7)`} sx={{ bgcolor: alpha('#43a047', 0.12), color: '#2e7d32', fontWeight: 600 }} />
          <Typography variant="caption" color="text.secondary">{num(p.total)} respostas</Typography>
        </Box>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={dados} margin={{ top: 8, right: 8, bottom: 4, left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
            <XAxis dataKey="nota" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <RechartsTooltip formatter={(v: any, _n: any, ctx: any) => [`${num(Number(v))} (${pct(ctx?.payload?.pct)})`, 'Respostas']} labelFormatter={(l) => `Nota ${l}`} />
            <Bar dataKey="qtd" name="Respostas" radius={[4, 4, 0, 0]}>
              {dados.map((d) => <Cell key={d.nota} fill={corNota(d.nota)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ─── Card de pergunta de selecao unica: distribuicao de opcoes ───

const SelecaoCard: React.FC<{ p: PerguntaSelecao }> = ({ p }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{p.enunciado}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{num(p.total)} respostas</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {p.opcoes.map((o, i) => (
            <Box key={o.descricao}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="body2" fontWeight={500}>{o.descricao}</Typography>
                <Typography variant="body2" color="text.secondary">{num(o.qtd)} • {pct(o.pct)}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, o.pct)}
                sx={{
                  height: 10, borderRadius: 5, bgcolor: alpha(theme.palette.text.primary, 0.06),
                  '& .MuiLinearProgress-bar': { borderRadius: 5, backgroundColor: OPC_COLORS[i % OPC_COLORS.length] },
                }}
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudePesquisaSatisfacao: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<Satisfacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naoConfigurado, setNaoConfigurado] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Seletor de periodo
  const [periodo, setPeriodo] = useState<PeriodoOpt>('tudo');
  const [inicioCustom, setInicioCustom] = useState<string>(isoDaysAgo(30));
  const [fimCustom, setFimCustom] = useState<string>(hojeISO());

  // Resolve o intervalo [inicio, fim] a partir do seletor (undefined = sem corte).
  const resolverPeriodo = useCallback((): { inicio?: string; fim?: string } => {
    if (periodo === 'tudo') return {};
    if (periodo === 'custom') return { inicio: inicioCustom, fim: fimCustom };
    return { inicio: isoDaysAgo(parseInt(periodo, 10)), fim: hojeISO() };
  }, [periodo, inicioCustom, fimCustom]);

  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const { inicio, fim } = resolverPeriodo();
      const result = await saudeService.getPesquisaSatisfacao(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        inicio,
        fim,
      });
      setNaoConfigurado(result?.configurado === false);
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar a pesquisa de satisfacao.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, resolverPeriodo]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar municipio, unidade ou periodo
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar a pesquisa de satisfacao.</Alert></Box>;
  }

  const kpis = data?.kpis;
  const notas = data?.notas || [];
  const selecao = data?.selecao || [];
  const semDados = !loading && (kpis?.total_respostas ?? 0) === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SentimentSatisfiedAltIcon color="primary" /> Pesquisa de Satisfacao
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Visao consolidada — todas as unidades do municipio'}
            {' • Satisfacao de pacientes e acompanhantes (apenas totais e agregacoes)'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Proxima atualizacao em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Seletor de periodo */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <ToggleButtonGroup
          size="small" exclusive value={periodo}
          onChange={(_e, v) => { if (v) setPeriodo(v); }}
          sx={{ flexWrap: 'wrap' }}
        >
          <ToggleButton value="tudo">Tudo</ToggleButton>
          <ToggleButton value="30">30 dias</ToggleButton>
          <ToggleButton value="90">90 dias</ToggleButton>
          <ToggleButton value="365">12 meses</ToggleButton>
          <ToggleButton value="custom">Personalizado</ToggleButton>
        </ToggleButtonGroup>
        {periodo === 'custom' && (
          <>
            <TextField type="date" size="small" label="Inicio" InputLabelProps={{ shrink: true }}
              value={inicioCustom} onChange={(e) => setInicioCustom(e.target.value)} />
            <TextField type="date" size="small" label="Fim" InputLabelProps={{ shrink: true }}
              value={fimCustom} onChange={(e) => setFimCustom(e.target.value)} />
          </>
        )}
      </Box>

      {naoConfigurado && <PainelNaoConfigurado />}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={6} md={3}><KpiMini label="Respostas" value={num(kpis?.total_respostas)} icon={<ForumIcon />} color={COR_PRIMARIA} loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiMini label="Satisfacao (nota ≥ 7)" value={pct(kpis?.taxa_satisfacao)} icon={<ThumbUpAltIcon />} color="#43a047" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiMini label="Nota media geral" value={`${nota(kpis?.media_geral)}/10`} icon={<StarIcon />} color="#ffb300" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiMini label="Pesquisas com resposta" value={num(kpis?.pesquisas_ativas)} icon={<PollIcon />} color="#7b1fa2" loading={loading} /></Grid>
      </Grid>

      {semDados && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma resposta de pesquisa de satisfacao no periodo
          {unidadeSelecionada ? ` para a unidade ${unidadeSelecionada.nome}` : ''}.
        </Alert>
      )}

      {/* Perguntas de nota (0-10) */}
      {(loading || notas.length > 0) && (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1 }}>Avaliacoes por nota (0 a 10)</Typography>
          <Grid container spacing={2}>
            {loading && notas.length === 0
              ? [0, 1, 2].map((i) => (
                  <Grid item xs={12} md={6} key={i}><Skeleton variant="rounded" height={280} /></Grid>
                ))
              : notas.map((p) => (
                  <Grid item xs={12} md={6} key={p.enunciado}><NotaCard p={p} /></Grid>
                ))}
          </Grid>
        </>
      )}

      {/* Perguntas de selecao unica */}
      {(loading || selecao.length > 0) && (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1 }}>Avaliacoes por opcao</Typography>
          <Grid container spacing={2}>
            {loading && selecao.length === 0
              ? [0, 1].map((i) => (
                  <Grid item xs={12} md={6} key={i}><Skeleton variant="rounded" height={200} /></Grid>
                ))
              : selecao.map((p) => (
                  <Grid item xs={12} md={6} key={p.enunciado}><SelecaoCard p={p} /></Grid>
                ))}
          </Grid>
        </>
      )}

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label="Satisfacao = % de notas ≥ 7 • Apenas totais e agregacoes (sem dados de identificacao do respondente)" />
    </Box>
  );
};

export default SaudePesquisaSatisfacao;
